import React, { useEffect, useState } from 'react';
import { v4 as generateUuid } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import styled from '@emotion/styled';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import Button from '@atlaskit/button';
import { Text } from '@atlaskit/primitives';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { token } from '@atlaskit/tokens';
import { N500 } from '@atlaskit/theme/colors';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import { ValueType } from '@atlaskit/select';
import { Offering, PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { ChargeElementData } from '@atlassian/cssxp-ccp-types';
import { ProductOffering } from '@css-xp/graphtypes/src/generated-types-sxp';
import {
  isLoomProduct,
  isOfferingIncluded,
  isStatusPageAddOnProduct,
  loomSortedOfferingList,
  ROVO_PAID_OFFERING_ID,
} from '@css-xp/ppc-utils';
import { usePartnerCatalogueContext } from '@css-xp/ppc-checkout/src/context/partner-catalogue-context';
import { formatLineItems } from '@css-xp/ppc-checkout/src/utils';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { errorNotificationBuilder, NotificationTypes, useFeatureToggle, useNotification } from '@css-xp/base';
import { transformLineItemsToCartApp, transformLineItemsToCartProduct, truthy } from '../../../../utils';
import {
  useAddNewProductStore,
  useCloudSiteOrOrgNameMapStore,
  useProductsForCloudSiteStore,
  useStore,
} from '../../../../store';
import { CartLinkedEntitlementsSchema, CartProductSchema } from '../../../../types';
import { CloudSitesListContainer } from './cloud-sites-list-container';
import {
  CloudSiteOrOrgNameMap,
  CloudSiteOrOrgNameMapGroupedByBillingProfile,
} from '../../../billing-profile/src/types';
import { processEntity } from '../../../entitlement-transfer/src/utils/api';
import {
  AddNewProductsWrapper,
  getContainerIdFromExistingJiraProducts,
  getExistingJiraProduct,
} from '../add-new-products-wrapper';
import { CartCatalogueLineItem, CartCatalogueLineItems, CatalogueContextItems } from '../types';
import { NEW_CLOUD_SITE_ID } from '../../../../constants';
import { isNewEntitlement } from '../../../../store/utils';
import { getTimestampFromLinkedProductsOrApps } from '../../../../store/utils/get-timestamp-from-linked-products-apps';
import { State } from '../../../../store/types';

interface AddNewProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer?: { name: string; ids: string[] } | null;
  cloudSiteHostName?: string;
  cloudSiteId?: string;
  isBillingProfileDetailsLoading?: boolean;
  billingProfileError?: any;
  fetchEntitlementListError?: any;
  isFetchEntitlementListLoading?: boolean;
}

export interface CloudSiteInfo {
  cloudSiteId?: string;
  cloudSiteHostName?: string;
}

type CatalogueOffering = Offering & {
  pricingType: string;
  chargeElements: Array<{ type: string; data: ChargeElementData }>;
};

enum SCREEN_NAME {
  SELECT_SITE,
  ADD_PRODUCTS,
}

const ModalHeaderWrapper = styled.div`
  width: 100%;
`;

const ModalTitleWrapper = styled.div`
  width: 100%;
`;

const ModalFooterWrapper = styled.div`
  width: 100%;
`;

const sortCloudSiteListByHostName = (
  cloudsiteList: {
    cloudSiteId: string;
    cloudSiteHostName: string;
  }[]
) => {
  return cloudsiteList.sort((a, b) => a.cloudSiteHostName.localeCompare(b.cloudSiteHostName));
};

const getCloudSitesListFromMap = (dataArray: Array<CloudSiteOrOrgNameMap>) => {
  const cloudsiteList: {
    cloudSiteId: string;
    cloudSiteHostName: string;
  }[] = [];

  const uniqueCloudSiteIds = new Set<string>();

  dataArray.forEach((data) => {
    const processProducts = (products: any[]) => {
      products.forEach((product) => {
        if (product?.cloudSiteId && !isEmpty(product?.cloudSiteId) && product?.cloudSite) {
          if (!uniqueCloudSiteIds.has(product.cloudSiteId)) {
            uniqueCloudSiteIds.add(product.cloudSiteId);
            cloudsiteList.push({
              cloudSiteId: product.cloudSiteId,
              cloudSiteHostName: product.cloudSite,
            });
          }
        }
      });
    };

    Object.values(data).forEach((value) => {
      if (value?.products) {
        processProducts(value.products);
      }
      if (value?.inactiveProducts) {
        processProducts(value.inactiveProducts);
      }
    });
  });

  return sortCloudSiteListByHostName(cloudsiteList);
};

export const getDefaultOffering = ({
  lineItem,
  sandboxCloudSiteParentProductIds,
  isJPDAsJiraFamilyEnabled,
  isJSMStandardOfferingEnabled,
  isCompassPremiumEnabled,
  isLoomEnabled,
  isJPDPremiumOfferingEnabled,
}: {
  lineItem: any;
  sandboxCloudSiteParentProductIds: string[];
  isJPDAsJiraFamilyEnabled?: boolean;
  isJSMStandardOfferingEnabled?: boolean;
  isCompassPremiumEnabled?: boolean;
  isLoomEnabled?: boolean;
  isJPDPremiumOfferingEnabled?: boolean;
}) => {
  let availableOfferings: CatalogueOffering[] = defaultTo(get(lineItem, 'offering'), []);

  // Changing the offering order for Loom products - https://hello.jira.atlassian.cloud/browse/TTC-32245
  if (isLoomEnabled && isLoomProduct(lineItem.id)) {
    /* This will place the offerings in the order mentioned in the loomSortedOfferingList array at top
        without changing the order for rest of the offerings */
    availableOfferings = sortBy(availableOfferings, (offering) => {
      const index = loomSortedOfferingList.indexOf(offering.id);
      return index === -1 ? Infinity : index;
    });
  }

  /**
   * we are converting productId = offeringId for statuspage addons and saving in store.
   * we are making this change because all statuspage addons have same product id
   * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
   */
  if (isStatusPageAddOnProduct(lineItem.id) && lineItem.isAddOn) {
    const selectedOffering = availableOfferings?.find((offering) => offering.id === lineItem.offeringId);
    return {
      value: selectedOffering?.id,
      // @ts-ignore
      label: selectedOffering?.offeringGroup, // offeringGroup is the name of the offering for statuspage add-ons
    };
  }

  // Selecting the default offering for a product

  if (availableOfferings && availableOfferings?.length > 0) {
    const standardOffering: CatalogueOffering | undefined = (availableOfferings as CatalogueOffering[]).find(
      // there can be multiple offerings with name Standard, selecting the one which is in included list
      (offering: Offering) =>
        sandboxCloudSiteParentProductIds.includes(lineItem.id)
          ? offering.offeringName.toUpperCase() === 'STANDARD SANDBOX'
          : offering.offeringName.toUpperCase() === 'STANDARD' &&
            isOfferingIncluded(offering, {
              isEnterpriseOfferingEnabled: false,
              isJSMRepackagingEnabled: isJSMStandardOfferingEnabled,
              isCompassPremiumEnabled,
              isJPDPremiumOfferingEnabled,
              isJPDAsJiraFamilyEnabled,
            })
    );
    const rovoPaidOffering: CatalogueOffering | undefined = (availableOfferings as CatalogueOffering[]).find(
      (offering: Offering) => offering.id === ROVO_PAID_OFFERING_ID
    );

    if (standardOffering) {
      return {
        value: standardOffering.id,
        label: standardOffering.offeringName,
      };
    } else {
      // Selecting the default offering for a non-standard product based on the offering group else selecting the first offering
      const defaultOffering =
        availableOfferings?.find((offering: any) => offering?.offeringGroup?.toUpperCase() === 'DEFAULT') ||
        availableOfferings[0];
      const defaultNonStandardOffering: CatalogueOffering = rovoPaidOffering ? rovoPaidOffering : defaultOffering;
      return {
        value: defaultNonStandardOffering?.id,
        label: defaultNonStandardOffering?.offeringName,
      };
    }
  }
};

const updateLineItemsWithDefaultOffering = ({
  lineItems,
  sandboxCloudSiteParentProductIds,
  isJPDAsJiraFamilyEnabled,
  isJSMStandardOfferingEnabled,
  isCompassPremiumEnabled,
  isLoomEnabled,
  isJPDPremiumOfferingEnabled,
}: {
  lineItems: any;
  sandboxCloudSiteParentProductIds: string[];
  isJPDAsJiraFamilyEnabled?: boolean;
  isJSMStandardOfferingEnabled?: boolean;
  isCompassPremiumEnabled?: boolean;
  isLoomEnabled?: boolean;
  isJPDPremiumOfferingEnabled?: boolean;
}) => {
  return lineItems.map((lineItem: any) => {
    if (!lineItem?.isApp) {
      const offering = getDefaultOffering({
        lineItem,
        sandboxCloudSiteParentProductIds,
        isJPDAsJiraFamilyEnabled,
        isJSMStandardOfferingEnabled,
        isCompassPremiumEnabled,
        isLoomEnabled,
        isJPDPremiumOfferingEnabled,
      });
      return {
        ...lineItem,
        offeringKey: offering?.value,
        offeringName: offering?.label,
      };
    }

    return lineItem;
  });
};

const updateLineItemsWithTrialLength = (lineItems: CartCatalogueLineItems) => {
  return lineItems.map((lineItem: CartCatalogueLineItem) => {
    const currentOffering = lineItem.offering?.find((offering) => offering.id === lineItem.offeringKey);
    const trialLengthInDays = defaultTo(get(currentOffering, 'trialLengthInDays'), '');
    return {
      ...lineItem,
      trialLengthInDays,
    };
  });
};
/*
 * OrderItems consists of lineItems, productTrialMap and appsPromotionsIdsMap
 * These data coming from catalogue context provider
 * onAddToCart will transform catalogue line items to state store items
 */
const onAddToCart = ({
  orderItems,
  existingProductsForCloudSite,
  products,
  linkedEntitlements,
  cloudSiteHost,
  addNewProductToCart,
  isJPDAsJiraFamilyEnabled,
}: {
  orderItems: CatalogueContextItems;
  existingProductsForCloudSite: CartProductSchema[];
  products: State['products'];
  linkedEntitlements: State['linkedEntitlements'];
  cloudSiteHost: string;
  addNewProductToCart: (products: CartProductSchema[], linkedEntitlements: CartLinkedEntitlementsSchema[]) => void;
  isJPDAsJiraFamilyEnabled: boolean;
}) => {
  // FIXME :: productTrialMap is coming empty in `orderItems`, can be removed if not used for any case
  const { lineItems, productTrialMap, appsPromotionsIdsDetailsMap } = orderItems;

  // Getting jira family container id from existing products. If not present then generating new uuid
  const jiraContainerId =
    getContainerIdFromExistingJiraProducts({ existingProductsForCloudSite, isJPDAsJiraFamilyEnabled }) ??
    generateUuid();

  const existingJiraProduct = getExistingJiraProduct({ existingProductsForCloudSite, isJPDAsJiraFamilyEnabled });

  const newApps: CartLinkedEntitlementsSchema[] = [];
  const newProducts: CartProductSchema[] = [];
  const cloudSiteId = defaultTo(get(existingProductsForCloudSite, '[0].cloudSiteId'), '');
  lineItems.forEach((lineItem: CartCatalogueLineItem) => {
    const isJiraProduct = lineItem?.isItAJiraProduct;
    const containerId = isJiraProduct ? jiraContainerId : undefined;
    const containerOrProductId = containerId ?? lineItem.lineItemId;
    if (lineItem.isApp) {
      const parentEntitlementId = lineItem.parentEntitlementId;
      const trialLengthInDays = defaultTo(productTrialMap?.get(lineItem?.productKey), '');
      const promoCodeDetail = defaultTo(appsPromotionsIdsDetailsMap?.get(lineItem?.productKey), {});
      const promotions = !isEmpty(promoCodeDetail) ? [promoCodeDetail] : [];
      const isParentNew = isNewEntitlement({ operationType: products[parentEntitlementId].operationType });
      lineItem.nextBillingTimestamp = lineItem.nextBillingTimestamp
        ? lineItem.nextBillingTimestamp
        : getTimestampFromLinkedProductsOrApps({ parentEntitlementId, products, linkedEntitlements });
      if (isParentNew) {
        /* here we are assigning empty string to identify the app is belongs to new parent product
         * this is required to assign the check in getAppsPayload for the condition of relates from Entitlement id
         */
        lineItem.parentEntitlementId = '';
      }

      const newApp = transformLineItemsToCartApp(lineItem, trialLengthInDays, promotions);
      newApps.push({
        ...newApp,
        containerOrProductId: isJiraProduct ? jiraContainerId : parentEntitlementId,
      });
      return;
    }

    const trialLengthInDays = defaultTo(productTrialMap?.get(lineItem?.productKey), lineItem?.trialLengthInDays || '');

    const appItems = (() => {
      return lineItem?.appLineItems?.map((appLineItem) => {
        // getting trial length from the productTrialMap. Which is block scope
        const trialLengthInDays = defaultTo(productTrialMap?.get(appLineItem?.productKey), '');

        // getting apps promocode from the appsPromotionsIdsMap
        const promocodeDetail = defaultTo(appsPromotionsIdsDetailsMap?.get(appLineItem?.productKey), {});
        const promotions = !isEmpty(promocodeDetail) ? [promocodeDetail] : [];
        // adding this check same as we have if parent line item is directly added as app
        // this check is when app is added as child with any new product from catalog directly.
        appLineItem.nextBillingTimestamp = appLineItem.nextBillingTimestamp
          ? appLineItem.nextBillingTimestamp
          : isJiraProduct && existingJiraProduct
          ? getTimestampFromLinkedProductsOrApps({
              parentEntitlementId: existingJiraProduct.id,
              products,
              linkedEntitlements,
            })
          : '';
        const newApp = transformLineItemsToCartApp(appLineItem, trialLengthInDays, promotions);

        return {
          ...newApp,
          containerOrProductId,
        };
      });
    })();

    newApps.push(...appItems);

    newProducts.push(
      transformLineItemsToCartProduct({
        lineItem,
        containerId,
        trialLengthInDays,
        cloudSite: cloudSiteHost,
        cloudSiteId,
      })
    );
  });

  addNewProductToCart(newProducts, newApps);
};

export const AddNewProductsModal: React.FC<AddNewProductsModalProps> = ({
  isOpen,
  onClose,
  isBillingProfileDetailsLoading = false,
  billingProfileError,
  fetchEntitlementListError,
  isFetchEntitlementListLoading = false,
  cloudSiteHostName,
  cloudSiteId,
}) => {
  const {
    getSelectedPricingPlanForAProduct,
    productTrialMap,
    appsPromotionsIdsDetailsMap,
    sandboxCloudSiteParentProductIds,
  } = usePartnerCatalogueContext();
  const [{ products, linkedEntitlements, sidebarFilter }] = useStore();
  const { searchQuery } = sidebarFilter ?? {};

  const [cloudSiteOrOrgNameMapData, actions] = useCloudSiteOrOrgNameMapStore();
  const { addNewProductToCart } = useAddNewProductStore();

  const { cloudSiteOrOrgNameMap } = cloudSiteOrOrgNameMapData ?? {};
  const cloudSiteOrOrgNameMapArray = (cloudSiteOrOrgNameMap as CloudSiteOrOrgNameMapGroupedByBillingProfile)?.map(
    ({ cloudSiteOrOrgNameMap }) => cloudSiteOrOrgNameMap
  );

  const cloudSitesList = getCloudSitesListFromMap(cloudSiteOrOrgNameMapArray);

  const { notificationSent, sendSuccessNotification, setNotificationSent } = useNotification();

  const [checkedProductsList, setCheckedProductsList] = useState<PartnerProductCatalogueItem[]>([]);

  const [selectedProgressIndex, setSelectedProgressIndex] = useState(0);
  const [screenValues] = useState([SCREEN_NAME.SELECT_SITE, SCREEN_NAME.ADD_PRODUCTS]);
  const [selectedCloudSite, setSelectedCloudSite] = useState<ValueType<CloudSiteInfo, false>>();
  const [isEnabled] = useFeatureToggle();

  useEffect(() => {
    // This directly opens the add products page instead of the site selection
    // when the user is redirected from the add new button click in the cloud site container on the cart page.
    if (cloudSiteHostName && cloudSiteId) {
      setSelectedProgressIndex(1);
      setSelectedCloudSite({
        cloudSiteId,
        cloudSiteHostName,
      });
    }
  }, [cloudSiteHostName, cloudSiteId]);

  const existingProductsForCloudSite = useProductsForCloudSiteStore({
    cloudSiteHost:
      selectedCloudSite?.cloudSiteId === NEW_CLOUD_SITE_ID || !selectedCloudSite?.cloudSiteHostName
        ? ''
        : selectedCloudSite?.cloudSiteHostName,
  });

  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);
  const isJSMStandardOfferingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JSM_OFFERING_CHANGE);
  const isCompassPremiumEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM);
  const isLoomEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STANDALONE_LOOM_FOR_UOF);
  const isJPDPremiumOfferingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_PREMIUM_OFFERING);
  const isAppificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APPIFICATION);

  const triggerErrorNotification = (message = '', header = '') => {
    if (notificationSent) {
      return;
    }
    const notificationOptions = errorNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('transfer-error-notification')
      .withHeader(header)
      .withMessage(message)
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  };

  if (billingProfileError || fetchEntitlementListError) {
    triggerErrorNotification('Please try again or contact Partner Service Desk.', 'Could not fetch subscriptions');
  }

  const handlePrev = () => {
    if (selectedProgressIndex === SCREEN_NAME.ADD_PRODUCTS) {
      setSelectedProgressIndex(SCREEN_NAME.SELECT_SITE);
      setCheckedProductsList([]);
    }
  };

  const handleSubmit = async () => {
    if (selectedProgressIndex === SCREEN_NAME.SELECT_SITE) {
      setSelectedProgressIndex(SCREEN_NAME.ADD_PRODUCTS);
    }

    if (selectedProgressIndex === SCREEN_NAME.ADD_PRODUCTS) {
      const lineItems = formatLineItems(
        { product: checkedProductsList },
        getSelectedPricingPlanForAProduct,
        isJPDAsJiraFamilyEnabled
      );

      const updatedLineItemsWithDefaultOffering = updateLineItemsWithDefaultOffering({
        lineItems,
        sandboxCloudSiteParentProductIds,
        isJPDAsJiraFamilyEnabled,
        isJSMStandardOfferingEnabled,
        isCompassPremiumEnabled,
        isLoomEnabled,
        isJPDPremiumOfferingEnabled,
      });
      const updatedLineItemsWithTrialLength = updateLineItemsWithTrialLength(updatedLineItemsWithDefaultOffering);

      onAddToCart({
        orderItems: {
          lineItems: [...updatedLineItemsWithTrialLength] as CartCatalogueLineItems,
          productTrialMap,
          appsPromotionsIdsDetailsMap,
        },
        existingProductsForCloudSite,
        products,
        linkedEntitlements,
        cloudSiteHost:
          selectedCloudSite?.cloudSiteId === NEW_CLOUD_SITE_ID ||
          !selectedCloudSite?.cloudSiteHostName ||
          isEmpty(selectedCloudSite?.cloudSiteHostName)
            ? ''
            : selectedCloudSite?.cloudSiteHostName,
        addNewProductToCart,
        isJPDAsJiraFamilyEnabled,
      });
      onClose();
      setSelectedCloudSite(null);
      setSelectedProgressIndex(SCREEN_NAME.SELECT_SITE);
      setCheckedProductsList([]);
    }
  };

  const modalTitle =
    selectedProgressIndex === SCREEN_NAME.SELECT_SITE
      ? isAppificationEnabled
        ? 'Select a site for the subscription'
        : 'Select a site to add the product to'
      : `Add ${isAppificationEnabled ? 'Atlassian apps' : 'products'}`;
  const modalDescription =
    selectedProgressIndex === SCREEN_NAME.SELECT_SITE
      ? 'It can be an existing site, or a new one.'
      : `Select at least one ${isAppificationEnabled ? 'app' : 'product'} for ${selectedCloudSite?.cloudSiteHostName}`;

  const trimSearchQuery = (searchQuery: string) => {
    return processEntity(searchQuery, true).isValid ? searchQuery.trim() : searchQuery;
  };

  return (
    <ModalTransition>
      {isOpen && (
        <Modal testId="add-new-products-modal" shouldScrollInViewport height={690} width={752}>
          <ModalHeader>
            <ModalHeaderWrapper>
              <ModalTitleWrapper className="flex align-center justify-space-between">
                <ModalTitle>{modalTitle}</ModalTitle>
                <Button
                  appearance="subtle"
                  onClick={() => {
                    setSelectedCloudSite(null);
                    setSelectedProgressIndex(SCREEN_NAME.SELECT_SITE);
                    setCheckedProductsList([]);
                    onClose();
                  }}
                  testId="transfer-entitlements-close-modal-button"
                >
                  <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle', N500)} />
                </Button>
              </ModalTitleWrapper>
              <Text size="small" color="color.text.subtlest">
                {modalDescription}
              </Text>
            </ModalHeaderWrapper>
          </ModalHeader>
          <ModalBody>
            {selectedProgressIndex === SCREEN_NAME.SELECT_SITE && (
              <CloudSitesListContainer
                selectedCloudSite={selectedCloudSite}
                setSelectedCloudSite={setSelectedCloudSite}
                isBillingProfileDetailsLoading={isBillingProfileDetailsLoading}
                isFetchEntitlementListLoading={isFetchEntitlementListLoading}
                clearSearchQuery={() => {
                  actions.setSearchQuery('');
                }}
                onQueryChange={(searchQuery: string) => {
                  actions.setSidebarFilter({
                    searchQuery: trimSearchQuery(searchQuery),
                  });
                }}
                cloudSitesList={cloudSitesList}
                searchQuery={searchQuery}
              />
            )}
            {selectedProgressIndex === SCREEN_NAME.ADD_PRODUCTS && (
              <AddNewProductsWrapper
                cloudSiteId={selectedCloudSite?.cloudSiteId}
                cloudSiteHost={selectedCloudSite?.cloudSiteHostName || ''}
                isDisabled={true}
                showCatalogueInModal={true}
                checkedProductsList={checkedProductsList}
                setCheckedProductsList={setCheckedProductsList}
              />
            )}
          </ModalBody>
          <ModalFooter>
            <ModalFooterWrapper className="flex align-center justify-space-between">
              <Button
                isDisabled={selectedProgressIndex === 0 || (!!cloudSiteId && selectedProgressIndex === 1)}
                onClick={handlePrev}
                testId="add-new-prev-button"
              >
                Prev
              </Button>

              <ProgressIndicator
                testId="add-new-progress-indicator"
                values={screenValues}
                selectedIndex={selectedProgressIndex}
                size="default"
              />

              <Button
                onClick={handleSubmit}
                testId="add-new-next-button"
                appearance={selectedProgressIndex === SCREEN_NAME.SELECT_SITE ? 'default' : 'primary'}
                isDisabled={
                  selectedProgressIndex === SCREEN_NAME.SELECT_SITE ? !selectedCloudSite : !checkedProductsList.length
                }
              >
                {selectedProgressIndex === SCREEN_NAME.SELECT_SITE ? 'Next' : 'Add to order'}
              </Button>
            </ModalFooterWrapper>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
